import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { signOut } from "@/features/auth/api/identity-platform-auth";
import { useAuthSession } from "@/features/auth/hooks/use-auth-session";
import type { Deployment } from "@/features/deployment-selection/model/deployment";
import {
  DEPLOYMENTS_QUERY_KEY,
  fetchDeployments,
} from "@/features/deployment-selection/api/deployments-client";
import { ApiError } from "@/shared/api/api-error";

export function useDeployments() {
  const authSession = useAuthSession();
  const navigate = useNavigate();

  return useQuery<readonly Deployment[], Error>({
    queryKey: DEPLOYMENTS_QUERY_KEY,
    enabled: Boolean(authSession.data),
    queryFn: async () => {
      if (!authSession.data) {
        throw new Error("Session must be loaded before deployments can be fetched.");
      }

      try {
        return await fetchDeployments();
      } catch (error) {
        if (error instanceof ApiError && error.statusCode === 401) {
          await signOut();
          navigate("/login", { replace: true });
          throw new Error("Authentication expired. Please sign in again.");
        }

        throw error;
      }
    },
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.statusCode === 401 || error.statusCode === 403)) {
        return false;
      }

      return failureCount < 2;
    },
  });
}
